import { useTranslation } from "react-i18next";
import { authApi } from "../api/auth";
import { useAuth } from "../auth/AuthContext";
import { SUPPORTED_LOCALES, type SupportedLocale } from "./index";
import { flagEmoji } from "./countries";
import { Select } from "../components/Select";
import { emojiFontStack } from "../lib/emojiFont";

const LANG_LABEL: Record<SupportedLocale, string> = {
  en: "English",
  sl: "Slovenščina",
  de: "Deutsch",
  hr: "Hrvatski",
  sr: "Srpski",
  it: "Italiano",
  es: "Español",
  fr: "Français",
  ru: "Русский",
  tr: "Türkçe",
};

// Locale → country whose flag we use. English maps to GB.
const LANG_FLAG: Record<SupportedLocale, string> = {
  en: "GB",
  sl: "SI",
  de: "DE",
  hr: "HR",
  sr: "RS",
  it: "IT",
  es: "ES",
  fr: "FR",
  ru: "RU",
  tr: "TR",
};

export function localeFlag(loc: SupportedLocale): string {
  return flagEmoji(LANG_FLAG[loc]);
}

export function localeLabel(loc: SupportedLocale): string {
  return LANG_LABEL[loc];
}

/** Locales available to IMP-internal staff. Customers see the full list. */
const INTERNAL_LOCALES: SupportedLocale[] = ["en", "sl"];

export function LocaleSwitcher() {
  const { i18n, t } = useTranslation();
  const { state } = useAuth();
  const current = (i18n.resolvedLanguage ?? "en") as SupportedLocale;

  // Policy: IMP staff portal ships in SL + EN only; the full multi-language
  // matrix is reserved for customer accounts.
  const visibleLocales = (state.status === "authenticated" && state.user.impInternal)
    ? INTERNAL_LOCALES
    : SUPPORTED_LOCALES;

  async function handleChange(next: SupportedLocale) {
    await i18n.changeLanguage(next);
    if (state.status === "authenticated") {
      try {
        await authApi.updateLocale(next);
      } catch {
        // Non-fatal: UI already switched, server will catch up on next change.
      }
    }
  }

  return (
    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
      <span className="locale-label">{t("language.label")}</span>
      <Select
        value={current}
        onChange={(v) => handleChange(v as SupportedLocale)}
        ariaLabel={t("language.label")}
        style={{ minWidth: 150 }}
        options={visibleLocales.map((loc) => ({
          value: loc,
          label: LANG_LABEL[loc],
          left: (
            <span style={{ fontFamily: emojiFontStack }}>
              {localeFlag(loc)}
            </span>
          ),
        }))}
      />
    </label>
  );
}
